import {
  BlockStack,
  InlineStack,
  TextField,
  Select,
  Checkbox,
  Text,
  SkeletonBodyText,
  Box,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { AttendeeDetail, Country, Language } from "@features/events/types";
import { PhoneNumberField } from "@shared/ui";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { DatePickerField } from "@shared/ui";
import { useMemo } from "react";

interface OverviewCardProps {
  attendee?: Partial<AttendeeDetail>;
  countries: Country[];
  languages: Language[];
  onDataChange: (updates: Partial<AttendeeDetail>) => void;
  loading: boolean;
  fieldErrors?: Record<string, string>;
}

export default function OverviewCard({
  attendee,
  countries,
  languages,
  onDataChange,
  loading,
  fieldErrors = {},
}: OverviewCardProps) {
  const { t } = useTranslation();

  const countryOptions = useMemo(
    () =>
      (countries || []).map((country) => ({
        label: country?.name || "Unknown Country",
        value: country?.iso3 || "",
      })),
    [countries]
  );
  const languageOptions = useMemo(
    () =>
      (languages || []).map((language) => ({
        label: language?.englishLabel || "Unknown Language",
        value: language?.iso || "",
      })),
    [languages]
  );

  if (loading) {
    return <SkeletonBodyText lines={8} />;
  }
  return (
    <BlockStack gap="400">
      <Text variant="headingMd" as="h2">
        {t("attendeeDetails.overview.title")}
      </Text>

      <InlineStack gap="400" blockAlign="start">
        <Box width="48%">
          <TextField
            label={t("attendeeDetails.overview.firstName")}
            value={attendee?.firstName || ""}
            onChange={(value) => onDataChange({ firstName: value })}
            autoComplete="given-name"
            requiredIndicator
            error={fieldErrors.firstName}
          />
        </Box>
        <Box width="48%">
          <TextField
            label={t("attendeeDetails.overview.lastName")}
            value={attendee?.lastName || ""}
            onChange={(value) => onDataChange({ lastName: value })}
            autoComplete="family-name"
            requiredIndicator
            error={fieldErrors.lastName}
          />
        </Box>
      </InlineStack>

      <InlineStack gap="400" blockAlign="start">
        <Box width="48%">
          <Select
            label={t("attendeeDetails.overview.country")}
            options={countryOptions}
            value={attendee?.country || ""}
            onChange={(value) => onDataChange({ country: value ?? undefined })}
          />
        </Box>
        <Box width="48%">
          <TextField
            label={t("attendeeDetails.overview.city")}
            value={attendee?.city || ""}
            onChange={(value) => onDataChange({ city: value })}
            autoComplete="address-level2"
            error={fieldErrors.city}
          />
        </Box>
      </InlineStack>
      <InlineStack gap="400" blockAlign="start">
        <Box width="48%">
          <Select
            label={t("attendeeDetails.overview.language")}
            options={languageOptions}
            value={attendee?.languageCode || "bg"}
            onChange={(value) =>
              onDataChange({ languageCode: value ?? undefined })
            }
            helpText={t("attendeeDetails.overview.languageHelpText")}
          />
        </Box>
        <Box width="48%">
          <DatePickerField
            label={t("attendeeDetails.overview.dateOfBirth")}
            value={attendee?.dateOfBirth || ""}
            onChange={(value) => onDataChange({ dateOfBirth: value })}
            disabled={false}
          />
        </Box>
      </InlineStack>

      <TextField
        label={t("attendeeDetails.overview.email")}
        type="email"
        value={attendee?.email || ""}
        onChange={(value) => onDataChange({ email: value })}
        autoComplete="email"
        error={fieldErrors.email}
      />

      <PhoneNumberField
        phoneLabel={t("attendeeDetails.overview.phoneNumber")}
        value={
          attendee?.phoneCountryCode && attendee?.phoneNumber
            ? `${attendee.phoneCountryCode}${attendee.phoneNumber}`
            : ""
        }
        onChange={(value) => {
          try {
            const phoneObj = parsePhoneNumberWithError(value);
            if (phoneObj) {
              const cc = `+${phoneObj.countryCallingCode}`;
              const ln = phoneObj.nationalNumber;
              onDataChange({
                phoneCountryCode: cc,
                phoneNumber: ln,
              });
            }
          } catch (err) {
            console.log(err);
          }
        }}
        phoneError={fieldErrors.phoneNumber}
      />

      <BlockStack gap="200">
        <Checkbox
          label={t("attendeeDetails.overview.agreedMarketingEmail")}
          checked={attendee?.agreedMarketingEmail || false}
          onChange={(checked) =>
            onDataChange({ agreedMarketingEmail: checked })
          }
        />
        <Checkbox
          label={t("attendeeDetails.overview.agreedMarketingSMS")}
          checked={attendee?.agreedMarketingSMS || false}
          onChange={(checked) => onDataChange({ agreedMarketingSMS: checked })}
          helpText={t("attendeeDetails.overview.marketingPermissionText")}
        />
      </BlockStack>
    </BlockStack>
  );
}
